import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { usePageLayout } from '../hooks/usePageLayout'
import './Tripo3d.css'
import {
  bookmarkObject,
  unBookmarkObject,
  tripo3d_latest_model,
  tripo3d_Prompt2Model,
  getObjectBookmark,
} from '../sdk/tripo3d'
import { Locale, translate, useLang } from '../components/Locale'
import { Link, NavLink, useNavigate, useSearchParams } from 'react-router-dom'
import ObjectViewer from '../components/ObjectViewer'
import { useEffect } from 'react'
import ComboBox from '../components/ComboBox'
import { useToken } from '../hooks/useToken'

// Declare model-viewer as a custom element
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'model-viewer': any
    }
  }
}

function Tripo3d() {
  const { token } = useToken()

  const layoutOptions = useMemo(
    () => ({
      showSidebar: true,
      showHeader: true,
      showFooter: true,
      title: {
        en: 'Generate from Tripo3D',
        zh_cn: '从 Tripo3D 生成',
        zh_hk: '從 Tripo3D 生成',
      },
    }),
    [],
  )

  usePageLayout(layoutOptions)

  type modelOptions = 'tripo3d'

  const navigate = useNavigate()

  const [searchParams, setSearchParams] = useSearchParams()
  let object_id = +searchParams.get('id')!

  const [prompt, setPrompt] = useState('')
  // TODO support auto mode from server side
  const styleList = [
    {
      en: '(auto)',
      zh_cn: '(自动)',
      zh_hk: '(自動)',
    },
    {
      en: 'person:person2cartoon',
      zh_cn: '角色:卡通',
      zh_hk: '角色:卡通',
      preview_url: '/images/preview-person-cartoon.webp',
    },
    {
      en: 'object:clay',
      zh_cn: '物件:黏土',
      zh_hk: '物件:黏土',
      preview_url: '/images/preview-object-clay.webp',
    },
    {
      en: 'object:steampunk',
      zh_cn: '物件:蒸气庞克',
      zh_hk: '物件:蒸氣龐克',
      preview_url: '/images/preview-object-steampunk.webp',
    },
    {
      en: 'object:barbie',
      zh_cn: '物件:芭比',
      zh_hk: '物件:芭比',
      preview_url: '/images/preview-object-barbie.webp',
    },
    {
      en: 'object:christmas',
      zh_cn: '物件:圣诞',
      zh_hk: '物件:聖誕',
      preview_url: '/images/preview-object-christmas.webp',
    },
    {
      en: 'animal:venom',
      zh_cn: '生物:毒液',
      zh_hk: '生物:毒液',
      preview_url: '/images/preview-animal-venom.webp',
    },
    {
      en: 'gold',
      zh_cn: '金',
      zh_hk: '金',
      preview_url: '/images/preview-gold.webp',
    },
    {
      en: 'ancient_bronze',
      zh_cn: '古代青铜器',
      zh_hk: '古代青銅器',
      preview_url: '/images/preview-ancient-bronze.webp',
    },
  ]

  const [style, setStyle] = useState(styleList[0]['en'])
  const [isEditEnabled, setIsEditEnabled] = useState<boolean>(
    false || !object_id,
  )

  // Handle prompt received from ObjectViewer
  const handlePromptReceived = (receivedPrompt: string) => {
    setPrompt(receivedPrompt)
  }

  const generateModel = async (
    model: modelOptions,
    prompt: string,
    style: string,
  ) => {
    let modelStyle = style === styleList[0]['en'] ? null : style
    if (model == 'tripo3d' && modelStyle) {
      setIsEditEnabled(false)
      let res = await tripo3d_Prompt2Model({
        body: { prompt: prompt, style: modelStyle },
      })
      navigate(`/tripo3d?id=${res.object_id}`)
    } else {
      setIsEditEnabled(false)
      let res = await tripo3d_Prompt2Model({
        body: { prompt: prompt },
      })
      navigate(`/tripo3d?id=${res.object_id}`)
    }
  }

  const { lang } = useLang()

  const [hasBookmarked, setHasBookmarked] = useState<boolean>(false)

  const handleBookmarkObject = async (object_id: number) => {
    await bookmarkObject({ params: { object_id } })
    setHasBookmarked(true)
  }

  const handleUnBookmarkObject = async (object_id: number) => {
    await unBookmarkObject({ params: { object_id } })
    setHasBookmarked(false)
  }

  const [showcaseObjects, setShowcaseObjects] = useState<number[]>([])

  const previewContainerRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    previewContainerRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    })
  }, [object_id, previewContainerRef.current])

  useEffect(() => {
    const loadShowcase = async () => {
      const res = await tripo3d_latest_model({})
      setShowcaseObjects(res.object_id_array)
    }
    loadShowcase()

    const loadBookmark = async () => {
      const res = await getObjectBookmark({ params: { object_id: object_id } })
      setHasBookmarked(res.isBookmarked)
    }
    if (object_id) {
      loadBookmark()
    }
  }, [])

  return (
    <>
      {object_id !== 0 && (
        <>
          {hasBookmarked ? (
            <button
              className="bookmark-button"
              onClick={() => handleUnBookmarkObject(object_id)}
              title={translate(lang, {
                en: 'Remove bookmark',
                zh_cn: '移除书签',
                zh_hk: '刪除書籤',
              })}
            >
              <i className="fa-solid fa-bookmark"></i>
            </button>
          ) : (
            <button
              className="bookmark-button"
              onClick={() => handleBookmarkObject(object_id)}
              title={translate(lang, {
                en: 'Bookmark the scene',
                zh_cn: '收藏此场景',
                zh_hk: '收藏此場景',
              })}
            >
              <i className="fa-regular fa-bookmark"></i>
            </button>
          )}
        </>
      )}
      <div className="page-tripo3d">
        <div className="product preview-container" ref={previewContainerRef}>
          <div className="view card">
            {object_id ? (
              <ObjectViewer
                key={object_id}
                object_id={object_id}
                onPromptReceived={handlePromptReceived}
              />
            ) : (
              <div className="model-viewer">
                <span>Start generate your model !</span>
              </div>
            )}
          </div>

          <form
            className="inputs card"
            onSubmit={e => {
              e.preventDefault()
              generateModel('tripo3d', prompt, style)
            }}
          >
            <div className="prompt">
              <label>Prompt</label>
              <textarea
                name="prompt"
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                placeholder="Enter a prompt"
                maxLength={256}
                required
                readOnly={!isEditEnabled}
              />
            </div>
            <div className="style">
              <label>Style</label>
              <ComboBox
                className="combobox"
                buttonClassName="primary-button"
                data={styleList}
                value={style}
                onChange={option => setStyle(option.en)}
              />
            </div>

            {/*
             * TODO require login
             * redirect to /login?page=tripo3d&prompt=...&style=...
             * or allow guess to generate like the scene (image, music)
             */}

            {(!object_id || (object_id && isEditEnabled)) && (
              <button
                className="primary-button"
                type="submit"
                disabled={!token}
              >
                <Locale en="Generate" zh_cn="生成" zh_hk="生成" />
              </button>
            )}

            {!!object_id && !isEditEnabled && (
              <>
                <button
                  className="primary-button"
                  type="button"
                  onClick={() => setIsEditEnabled(true)}
                  disabled={!token}
                >
                  <Locale en="Remix" zh_cn="二次创作" zh_hk="二次創作" />
                </button>
              </>
            )}

            {!token && (
              <>
                <div className="login-required">
                  <Locale
                    en="Please login to generate"
                    zh_cn="请登录以生成"
                    zh_hk="請登入以生成"
                  />
                </div>
                <NavLink to="/login" className="primary-button">
                  <Locale en="Login" zh_cn="登录" zh_hk="登入" />
                </NavLink>
              </>
            )}
          </form>
        </div>
      </div>
    </>
  )
}

export default Tripo3d
